/* eslint-disable no-case-declarations */
// Import common types
import config from "../config.js";

// Import required modules
import { listen, dom, doms } from "../common/DOM.js";
import Debug from "../common/Debug.js";

/**
 * A class to handle the scrollable / swipeable family carousel.
 */
class FamilyCarousel {
  /**
   * The constructor for the class.
   */
  constructor(user) {
    // Reference to the current user and user service
    this.user = user;

    // Reference to the user profile data or the mock debug data
    this.profile = Debug.enabled ? Debug.readUserMockData() : this.user.data;

    // Placeholder for current families data
    this.families = [];

    // Placeholder for message display timer
    this.timer = null;

    // Cache the reference to the container
    this.carousel = dom(".carousel");
  }

  /**
   * Method to initialize carousel.
   */
  async initialize() {
    // Skip if no carousel present in the DOM
    if (!this.carousel) {
      return;
    }

    // Read the families
    await this.renderFamilies();

    // Handle polling for new data at the specified interval
    window.setInterval(async () => await this.renderFamilies(), config.polling.interval);

    // Watch for scroll and update pagination based on scroll amount
    listen(this.carousel, "scroll", () => {
      this.updatePagination(Math.round(this.carousel.scrollLeft / window.innerWidth));
    });

    // Handle on click out to close
    listen("body", "click", this.hideMemberPopovers.bind(this));
    listen("body", "click", this.hideStatusPopover.bind(this));

    // Handle on click of the Check In and status buttons
    listen(".button-checkin", "click", this.toggleStatusPopover.bind(this));
    listen(".button-checkin-status", "click", this.handleUpdateStatus.bind(this));

    // Handle on click of the add new family button
    listen(".button-add", "click", this.updateFamily.bind(this));

    // Show the carousel now that data is rendered
    this.carousel.classList.add("active");

    // Set the initial scroll position
    dom(".carousel").scrollLeft = 0;

    // Handle auto check-in if query string is present
    if (window.location.search.includes("check-in=true")) {
      // Clear the query string from the URL
      window.history.pushState({}, null, window.location.pathname);
      // Set the previous status and show the popover with message
      await this.autoCheckIn();
    }
  }

  /**
   * Method to get the families data.
   */
  async getFamilies(useCache = false) {
    if (useCache) {
      return this.families;
    }

    // Enable scenario specific debugging by appending #debug to the URL
    // TODO: Determine if there is a better approach for this debugging
    if (Debug.enabled) {
      // Pass hour offsets for each mock user to test various scenarios
      this.families = Debug.readFamiliesMockData({ offsetA: -3, offsetB: -25, offsetC: -35 });
    } else {
      this.families = (await this.user.readFamilies()) || [];
    }

    return this.families;
  }

  /**
   * Method to render families.
   */
  async renderFamilies() {
    await this.getFamilies();

    // Debug.log("FamilyCarousel.renderFamilies: data", this.families);

    // Set a buffer to show the yellow warning state for X hours
    const buffer = config.status.overdueBuffer;

    // Get references to the carousel container
    const container = dom("[data-carousel-wrapper]", this.carousel);
    let template = "";

    for (let familyIndex = 0; familyIndex < this.families.length; familyIndex++) {
      const family = this.families[familyIndex];
      let members = "";

      family.members.forEach((member, memberIndex) => {
        const id = `family-${familyIndex}-member-${memberIndex}`;
        const checkin = this.formatCheckInTimestamp(member.updatedAt);
        const remaining = checkin.remaining;
        const pending = member.memberState === config.MEMBER_STATE.INVITED;

        let color = "";
        if (remaining <= (buffer * -1)) {
          color = "red";
        // Show warning for the first "buffer" part of missed check-in (3 hours)
        } else if (remaining < 0) {
          color = "yellow";
        } else {
          color = "green";
        }

        let mood = "";
        let icon = "";
        switch (member.mood) {
          case config.USER_MOOD.USER_FROWN:
            mood = "Frown";
            icon = "frown";
            break;
          case config.USER_MOOD.USER_NEUTRAL:
            mood = "Neutral";
            icon = "neutral";
            break;
          case config.USER_MOOD.USER_SMILE:
            mood = "Smile";
            icon = "smile";
            break;
        }

        // Check to see if the use is in invited state
        if (pending) {
          mood = "Invited";
          icon = "invited";
          color = "grey";
        }

        // TODO: Move text content to the CMS / data JSON file
        const details = pending
          ? [
            '<div class="family-member-details-checkin">',
              'Pending Invitation Acceptance',
            '</div>',
          ].join("")
          : [
            '<div class="family-member-details-checkin">',
              checkin.formatted,
            '</div>',
            '<div class="family-member-details-next">',
              `<b>${remaining < 0 ? "Check In Late" : "Next Check In"}:</b>`,
              checkin.display,
            '</div>',
          ].join("");

        const pathData = pending ? null : this.createProgressSvgPath(remaining);

        const progress = pending
        ? "" : [
          '<svg class="family-member-status-progress" viewBox="0 0 70 70">',
            `<path id="family-member-status-progress-${id}" d="${pathData}" />`,
          '</svg>',
        ].join("");

        members += [
          `<li class="family-member ${color}" data-status="${member.mood}" data-id="${id}" data-family="${familyIndex}" data-member="${memberIndex}">`,
            '<div class="family-member-status">',
              '<div class="family-member-status-icon">',
                `<img src="files/icon-status-${icon}.svg" alt="${icon}">`,
              '</div>',
              progress,
            '</div>',
            '<div class="family-member-details">',
              '<div class="family-member-details-name">',
                member.memberState === 8 ? member.emailMasked : `${member.firstName} ${member.lastName}`,
              '</div>',
              details,
            '</div>',
          '</li>',
        ].join("");
      });

      template += [
        '<li class="families-item" data-carousel-item="', family.familyId, '">',
          '<section class="wrapper">',
            "<h1>", family.familyName, "</h1>",
            '<button class="families-item-add button"',
                'data-action="add"',
                'data-family-id="', family.familyId, '">',
              "Add",
            "</button>",
            '<ul class="family">',
              members,
            "</ul>",
          "</section>",
        "</li>",
      ].join("");
    }

    // Remove all children first, except the last one (add new screen)
    [...container.children].slice(0, -1).forEach((child) => container.removeChild(child));

    // Insert new template string before the last element
    container.insertAdjacentHTML("afterbegin", template);

    // Handle on click of the family member status to toggle popover
    listen(".families-item-add", "click", this.addFamilyMember.bind(this));

    // Render the updated pagination
    this.renderPagination();
  }

  /**
   * The method to describe a circular sector (pie slice) value for a SVG path.
   */
  createProgressSvgPath(remaining) {
    // TODO: Get radius as the pixel width of the status container from DOM
    const radiusPixels = 70;
    const centerX = radiusPixels / 2;
    const centerY = radiusPixels / 2;

    // Get the checkin remaining hours
    const hoursInDay = 24;
    // Use just under 24 as max, as a full 24 would be equivalent to 0 visually
    const remainingCapped = Math.max(Math.min(remaining, 23.99), 0);
    const remainingAmount = hoursInDay - remainingCapped;
    const remainingRatio = remainingAmount / hoursInDay;

    // Start angle is normalized so zero is twelve on the clock
    // Use just under 360, as that times ratio 1 would be equivalent to 0 visually
    let startAngle = 359.9 * remainingRatio;
    // Calculate end angle based on check in date
    let endAngle = 0;

    // Offset so that 0 degree angles above are 12 on the clock
    startAngle -= 90;
    endAngle -= 90;

    // Get the radian unit for calculations below
    const radianUnit = Math.PI / 180;

    // Calculate the path coordinates
    const x1 = centerX + radiusPixels * Math.cos(radianUnit * startAngle);
    const y1 = centerY + radiusPixels * Math.sin(radianUnit * startAngle);
    const x2 = centerX + radiusPixels * Math.cos(radianUnit * endAngle);
    const y2 = centerY + radiusPixels * Math.sin(radianUnit * endAngle);

    // Specify the path arc direction based angle
    const arc = endAngle - startAngle <= 180 ? "1" : "0";

    // Return the values formatted as SVG path data
    return [
      `M ${centerX} ${centerY}`,
      `L ${x1} ${y1}`,
      `A ${radiusPixels} ${radiusPixels} 0 ${arc} 1 ${x2} ${y2}`,
      `Z`,
    ].join(" ");
  }

  /**
   * The method to handle toggling status popover.
   */
  toggleStatusPopover(event) {
    event.stopPropagation();
    const active = dom("#actionbar-checkin").getAttribute("aria-expanded") === "true";

    if (active) {
      this.hideStatusPopover();
    } else {
      this.autoCheckIn();
    }
  }

  /**
   * The method to handle showing status popover.
   */
  showStatusPopover(showMessage = false) {
    dom("#actionbar-checkin").setAttribute("aria-expanded", "true");
    dom("#actionbar-popover").setAttribute("aria-hidden", "false");

    if (!showMessage) {
      return;
    }

    const message = dom(".actionbar-popover-message");
    const counter = dom(".actionbar-popover-message-dismiss span");

    let time = 10;

    counter.innerText = time;
    message.classList.add("active");

    this.timer = window.setInterval(() => {
      time -= 1;
      counter.innerText = time;
      if (time === 0) {
        this.hideStatusPopover();
      }
    }, 1000);

    doms(".button-checkin-status").forEach((button) => {
      let active = false;
      for (let key in config.USER_MOOD) {
        if (key === button.dataset.mood && this.profile.mood === config.USER_MOOD[key]) {
          active = true;
        }
      }
      button.classList.toggle("active", active);
    });
  }

  /**
   * The method to handle hiding status popover.
   */
  hideStatusPopover() {
    doms(".button-checkin-status").forEach((button) => {
      button.classList.remove("active");
    });

    dom("#actionbar-checkin").setAttribute("aria-expanded", "false");
    dom("#actionbar-popover").setAttribute("aria-hidden", "true");

    dom(".actionbar-popover-message").classList.remove("active");

    window.clearInterval(this.timer);
  }

  /**
   * The method to handle updating status.
   */
  async handleUpdateStatus(event) {
    event.preventDefault();
    event.stopPropagation();

    const mood = config.USER_MOOD[event.currentTarget.dataset.mood];
    this.updateStatus(mood);
  }

  /**
   * The method to handle updating status.
   */
  async updateStatus(status = -1) {
    // Default to the previous mood
    if (status < 0) {
      status = this.profile.mood;
    }

    // TODO: Handle errors and set correct location
    await this.user.updateUserStatus({ status, location: { lat: 0, lng: 0 } });

    // Update the cached value to skip a network call
    this.profile.mood = status;

    // Hide the status popover after the status has been set
    this.hideStatusPopover();

    // Re-render the families
    this.renderFamilies();
  }

  /**
   * The method to handle checking in by setting status.
   */
  async autoCheckIn(event) {
    // Prevent bubbling to body if coming from click handler
    event && event.stopPropagation();

    // Set the previous status
    await this.updateStatus();

    // Show the popover with message
    this.showStatusPopover(true);
  }

  /**
   * The method to handle adding/updating new families.
   */
  async updateFamily({ target }) {
    // The button has multiple uses
    switch (target.dataset.action) {
      // Add a new family
      case "add":
        const response = await this.user.createFamily({
          name: dom("#input-family-add").value,
        });

        if (!response.ok) {
          Debug.log("UserService.updateFamily: ", response);
          return window.alert("An error occurred.");
        }

        this.familyId = (await response.json()).familyId;

        // Create the list item and input element
        const li = document.createElement("li");
        const input = this.createMemberInput();

        // Append the input to the list item and to the list
        li.appendChild(input);
        dom(".family-add-members").appendChild(li);

        // Update the button state
        target.dataset.action = "update";
        target.innerText = "Save";

        // Focus on the next input item
        input.focus();

        break;

      // Update the family
      case "update":
        const inputs = doms(".family-add-members .family-add-input");
        let success = true;

        for (let index = 0; index < inputs.length; index++) {
          const inp = inputs[index];
          if (!inp.value) {
            continue;
          }
          const res = await this.user.createFamilyMember(this.familyId, {
            username: inp.value,
          });
          // TODO: Should error stop adding the rest?
          if (!res.ok) {
            success = false;
            // continue;
          }
          inp.value = "";
          // NOTE: This commented code will temporarily remain for debugging purposes
          // // Get the response data to use signed invitation
          // const invitation = await response.json();
          // // Confirm the family member to bypass the email verification
          // await this.user.confirmFamilyMember(this.familyId, invitation);
        }

        if (!success) {
          return window.alert("An error occurred.");
        }

        // Reset the family name
        // TODO: Add a form element and use .reset()
        dom("#input-family-add").value = "";

        // Re-render the families
        this.renderFamilies();

        break;
    }
  }

  /**
   * The method to handle adding new family members to the family list.
   */
  async addFamilyMember({ target }) {
    const { familyId, action } = target.dataset;
    const list = dom(`.families-item[data-carousel-item="${familyId}"] .family`);

    // TODO: Refactor this with the updateFamily method

    // The button has multiple uses
    switch (action) {
      // Add a new family
      case "add":
        // Create the list item and input element
        const li = document.createElement("li");
        const input = this.createMemberInput(false);

        // Append the input to the list item and to the list
        li.appendChild(input);
        list.appendChild(li);

        // Update the button state
        target.dataset.action = "update";
        target.innerText = dom(".family-add-header .button-add").dataset.update;

        break;

      // Update the family
      case "update":
        const inputs = doms(".family-add-input", list);
        let success = true;

        for (let index = 0; index < inputs.length; index++) {
          const inp = inputs[index];
          if (!inp.value) {
            continue;
          }
          const response = await this.user.createFamilyMember(familyId, {
            username: inp.value,
          });
          // TODO: Should error stop adding the rest?
          if (!response.ok) {
            success = false;
          }
        }

        if (!success) {
          return window.alert("An error occurred.");
        }

        // Re-render the families
        this.renderFamilies();

        break;
    }
  }

  /**
   * The method to handle adding new inputs to the family list.
   */
  addMemberInput() {
    const list = dom(".family-add-members");
    const inputs = doms("input", list);
    const last = inputs[inputs.length - 1];

    if (last.value === "") {
      return;
    }

    // Create the list item and input element
    const li = document.createElement("li");
    const input = this.createMemberInput();

    // Append the input to the list item and to the list
    li.appendChild(input);
    list.appendChild(li);
  }

  /**
   * The method to handle creating new inputs for the family list.
   */
  createMemberInput(addListeners = true) {
    const input = document.createElement("input");

    input.type = "email";
    input.className = "family-add-input";
    input.placeholder = "Family Member Email Address";

    if (addListeners) {
      input.addEventListener("input", this.addMemberInput.bind(this));
      input.addEventListener("blur", this.removeMemberInput.bind(this));
    }

    return input;
  }

  /**
   * The method to handle removing empty inputs.
   */
  removeMemberInput() {
    const list = dom(".family-add-members");
    const inputs = doms("input", list);

    for (let index = 0; index < inputs.length - 1; index++) {
      const input = inputs[index];
      if (input.value === "") {
        input.parentNode.parentNode.removeChild(input.parentNode);
      }
    }
  }

  /**
   * The method to handle toggling the family member popover.
   */
  toggleMemberPopover({ target }) {
    const id = target.getAttribute("aria-controls");
    const expand = target.getAttribute("aria-expanded") === "false";

    this.toggleMemberPopoverById(id, expand);
  }

  /**
   * The method to handle toggling the family member popover by ID.
   */
  toggleMemberPopoverById(id, expand) {
    const buttons = doms(".family-member-toggle");
    const toggle = dom(`button[aria-controls='${id}']`);
    const popover = dom("#" + id);

    if (!expand) {
      toggle.setAttribute("aria-expanded", "false");
      popover.setAttribute("aria-hidden", "true");
      popover.classList.remove("above", "below");
      return;
    }

    buttons.forEach((button) => {
      button.setAttribute("aria-expanded", "false");
      const controls = dom("#" + button.getAttribute("aria-controls"));
      controls.setAttribute("aria-hidden", "true");
      controls.classList.remove("above", "below");
    });

    toggle.setAttribute("aria-expanded", "true");
    popover.setAttribute("aria-hidden", "false");

    if (toggle.getBoundingClientRect().top < window.innerHeight / 2) {
      popover.classList.add("below");
    } else {
      popover.classList.add("above");
    }
  }

  /**
   * The method to handle hiding the family member popovers.
   */
  hideMemberPopovers({ target }) {
    const popovers = doms(".family-member-popover[aria-hidden='false']");

    // Loop over all popovers
    popovers.forEach((element) => {
      // Skip if the click is on the toggle button
      if (target.classList.contains("family-member-toggle")) {
        return;
      }
      // Skip if the click is within the active popover
      if (element.contains(target)) {
        return;
      }
      // Hide the current popover
      this.toggleMemberPopoverById(element.id, false);
    });
  }

  /**
   * Method to render pagination dots.
   */
  renderPagination() {
    // Get references to the pagination container and count
    const container = dom("[data-carousel-pagination]", this.carousel);
    const count = doms("[data-carousel-item]", this.carousel).length;

    // Skip if no container found in the DOM
    if (!container) {
      return;
    }

    // Clear previous dots and set the initial heading
    // TOOD: Move text content to the CMS / data JSON file
    container.innerHTML = "<h2>Families / Groups</h2>";

    // Create the list element
    const list = document.createElement("ul");
    list.className = "pagination-list";

    // Create a dot for each item in the swipeable component
    for (let index = 0; index < count; index++) {
      // Create the list item element
      const item = document.createElement("li");
      // Create the pagination element
      const dot = document.createElement("button");

      // Set the classes, a11y attributes and text label
      dot.classList.add("dot");
      dot.classList.toggle("active", index === 0);
      dot.tabIndex = index === 0 ? 0 : -1;
      dot.setAttribute("aria-label", `Go to slide ${index + 1}`);
      dot.setAttribute("data-carousel-pagination-item", "");
      dot.innerText = index < count - 1 ? index + 1 : "+";

      // Add a click event listener to navigate to the respective index
      listen(dot, "click", () => {
        this.carousel.scrollTo({
          left: index * window.innerWidth,
          behavior: "smooth",
        });
      });

      // Enable keyboard interaction, allowing navigation using the Enter/Space keys
      listen(dot, "keydown", (event) => {
        if (event.code === "Enter" || event.code === "Space") {
          dot.click();
        }
      });

      // Append to the list item
      item.appendChild(dot);

      // Append to the list of dots
      list.appendChild(item);

    }

    // Append to the container
    container.appendChild(list);
  }

  /**
   * Method to update pagination dots based on the index.
   * @param {number} index - Index of the active dot.
   */
  updatePagination(index) {
    // Query all pagination items within the current carousel
    const dots = doms("[data-carousel-pagination-item]", this.carousel);

    // Remove active class from all dots
    dots.forEach((dot) => {
      dot.classList.remove("active");
      dot.removeAttribute("aria-current");
    });

    // Add active class to the dot representing the index
    dots[index].classList.add("active");
    dots[index].setAttribute("aria-current", "true");
  }

  /**
   * Method to format the timestamp date and create the check-in ratio.
   * @param {String} timestamp - The date timestamp with timezone offset.
   */
  formatCheckInTimestamp(timestamp) {
    // Create a Date object using the timestamp string
    const date = new Date(timestamp);

    // Specify the options for formatting the date for display
    const options = {
      month: "long",
      weekday: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };

    // Create the formatter instance and format the date
    const formatter = new Intl.DateTimeFormat("en-US", options);
    const formatted = formatter.format(date);

    // Define constants for time calculations
    const hour = 60 * 60 * 1000;
    const day = 24 * hour;

    // Get the current date/time and 24 hours ahead
    const now = new Date();
    const tomorrow = new Date(date.getTime() + day);

    // Calculate the next check-in time remaining
    const remaining = (tomorrow - now) / hour;

    // Format the display time
    const display = this.getTimeString(remaining);

    return { formatted, remaining, display };
  }

  /**
   * Method to format the remaining display time.
   */
  getTimeString(remaining) {
    // Handle negative time (time ago)
    const isNegative = remaining < 0;
    // Make remaining positive for calculation
    remaining = Math.ceil(Math.abs(remaining));

    // Define time units (in hours)
    const units = [
      { label: "Year", value: 365 * 24 },
      { label: "Month", value: 30 * 24 },
      { label: "Week", value: 7 * 24 },
      { label: "Day", value: 24 },
      { label: "Hour", value: 1 }
    ];

    // Get the appropriate time unit
    for (let { label, value } of units) {
      const time = Math.floor(remaining / value);
      if (time >= 1) {
        return `${time} ${label}${time > 1 ? "s" : ""}${isNegative ? " Ago" : ""}`;
      }
    }

    // Otherwise return the hours
    return `${remaining} Hour${remaining > 1 ? "s" : ""}${isNegative ? " Ago" : ""}`;
  }
}

export default FamilyCarousel;
